import API from "../../../../API";
import toaster from "../../../common/toaster";
import {getLayerIdLocalStorageKey, getPublishFilterLocalStorageKey} from "../../../../../utils/publish";
import {transformAPIResponseIntoCapitalizedJSON, transformCapitalizedJSONIntoSnakeCase} from "../../../../utility";

const API_ROUTE = {
    GET: '/content/publish/get',
    UPDATE: '/content/publish/update',
    DELETE: '/content/publish/delete',
    BULK_DELETE: '/content/publish/bulk-delete',
    UNDELETE: '/content/publish/undelete',
    ADD: '/content/publish/insert',
    UNLINK: '/content/campaign/convert',
    REJECT: '/content/publish/reject',
    PREVIEW: '/content/publish/preview'
}

const translations = {
    updateFailed: Lang.langGet("Failed to update layer elements."),
    reorderFailed: Lang.langGet("Failed to reorder layer elements."),
    outdated: Lang.langGet("Layer elements outdated. Please reload the layer."),
    unlinkFailed: Lang.langGet("Failed to convert the link to a local copy!"),
    unlinked: Lang.langGet("Successfully converted to a local copy"),
    rejected: Lang.langGet("All users that have access to this element will be notified per mail of this rejection."),
    rejectionFailed: Lang.langGet("The notification about this rejection could not be sent!")
}

const DEBUG = false;

//TODO: unify calendar and calendar Privileges into one class and pass it to calendar.vue and move businessHours into there (also createCalendarOptions)
export default class Calendar {
    constructor(layer, screen, screenGroup) {
        /** @type {number}  */
        this.layer = layer; // this is the layerNr
        this.screen = screen;
        this.screenGroup = screenGroup;
        this.project = Laravel.activeProject;
    }

    updateLayer(layerNr, layerId) {
        this.layer = layerNr;

        const key = getLayerIdLocalStorageKey();
        localStorage.setItem(key, JSON.stringify(layerId));
    }

    updateScreen(newScreen) {
        this.screen = newScreen;

        const key = getPublishFilterLocalStorageKey();
        const filter = {screen: this.screen, screengroup: this.screenGroup};
        localStorage.setItem(key, JSON.stringify(filter));
    }

    async get() {
        const data = {
            project: this.project,
            layer: this.layer,
            filter: {
                screen: this.screen,
                screengroup: this.screenGroup,
            }
        };

        const api = new API(API_ROUTE.GET, { dontKebabCase: true });
        const resultNoKebab = await api.post(data);
        const result = transformAPIResponseIntoCapitalizedJSON(resultNoKebab, true);

        if (!result.errored) {
            if (DEBUG) console.log('Fetched publish content', result);

            const elements = result.elements || [];
            const filteredElements = elements.filter(element => element.data.screens.includes(this.screen));

            if (DEBUG) console.log('Filtered publish content to screen', filteredElements);

            return {...result, elements: filteredElements};
        }
    }

    /**
     *
     * @param {CalendarPublishElement} element
     * @returns {Promise<{data: *, response: Response, errored: boolean}|*|string|{errored: boolean}>}
     */
    async update(element) {
        const currentId = element.id;

        if (DEBUG) console.log(`Update Element "${element.data.name}" with id ${currentId}`, element);

        this.saving = true;

        element.data.literal_times = true;

        const updateData = {
            id: currentId,
            project_id: Laravel.activeProject,
            data: element.data,
            change_count: element.changeCount,
            published_at: element.publishedAt,
            limit: {
                screen: this.screen,
                group: this.screenGroup
            }
        }

        const api = new API(API_ROUTE.UPDATE, { dontSnakeCase: true });

        const formatedData = transformCapitalizedJSONIntoSnakeCase(updateData, true);
        const result = await api.post(formatedData);

        if (!result.errored) {
            if (DEBUG) console.log(`Successfully updated "${element.data.name}" with id ${currentId}`, result);

            return result;
        }

        if (result.data?.data?.outdated) {
            toaster.error(translations.outdated);
        } else {
            toaster.error(translations.updateFailed);
        }

        return {state: 'error'};
    }

    /**
     *
     * @param {CalendarPublishElement} element
     * @returns {Promise<{data: *, response: Response, errored: boolean}|*|string|{errored: boolean}>}
     */
    async delete(element) {
        const api = new API(API_ROUTE.DELETE);

        const data = {
            project: Laravel.activeProject,
            element_id: element.id,
            layer_id: element.layerId,
        }

        const result = await api.post(data);

        if (!result.errored) {
            if (DEBUG) console.log(`Successfully deleted "${element.data.name}" with id ${element.id}`, result);

            return result;
        }

        // TODO error handling?
    }

    /**
     * Bulk deletes calendar publish elements.
     * @param {CalendarPublishElement[]} elements - Array of elements to delete
     * @returns {Promise<{data: *, response: Response, errored: boolean}|{errored: boolean, message: string}>}
     */
    async bulkDelete(elements) {
        if (!Array.isArray(elements) || elements.length === 0) {
            return { errored: true, message: 'No elements provided for deletion.' };
        }

        const api = new API(API_ROUTE.BULK_DELETE);
        const data = {
            element_ids: elements.map(e => e.id),
        };

        try {
            const result = await api.post(data);

            if (!result.errored) {
                if (DEBUG) console.log(`Successfully deleted ${elements.length} elements`, result);

                return result;
            } else {
                console.error('Bulk deletion API responded with error:', result);
                return { errored: true, message: 'Bulk deletion failed.', ...result };
            }
        } catch (error) {
            console.error('Bulk deletion failed due to exception:', error);
            return { errored: true, message: 'Bulk deletion encountered an exception.', error };
        }
    }


    /**
     *
     * @param {CalendarPublishElement} element
     * @returns {Promise<{data: *, response: Response, errored: boolean}|*|string|{errored: boolean}>}
     */
    async undelete(element) {
        const api = new API(API_ROUTE.UNDELETE);

        const data = {
            project: Laravel.activeProject,
            element_id: element.id,
            layer_id: element.layerId,
        }

        const result = await api.post(data);

        if (!result.errored) {
            if (DEBUG) console.log(`Successfully undeleted "${element.data.name}" with id ${element.id}`, result);
            // element.deleted_at = null;

            return result;
        }

        // TODO error handling?
    }

    /**
     *
     * @param {CalendarPublishElement} element
     * @returns {Promise<{data: *, response: Response, errored: boolean}|*|string|{errored: boolean}>}
     */
    async add(element) {
        const api = new API(API_ROUTE.ADD);

        element.data.literal_times = true;

        const data = {
            project: Laravel.activeProject,
            layer_id: element.layerId,
            element: element.data
        };

        const result = await api.post(data);

        if (!result.errored) {
            if (DEBUG) console.log(`Successfully added "${element.data.name}"`, result);

            return result;
        }

        return {state: 'error'};
    }

    /**
     *
     * @param {CalendarPublishElement} element
     * @returns {Promise<CalendarPublishElement | {state}>}
     */
    async unlink(element) {
        const api = new API(API_ROUTE.UNLINK);

        const data = {
            id: element.data.id,
            project: Laravel.activeProject,
            type: element.data.type
        };

        const result = await api.post(data);

        if (!result.errored) {
            element.data.state = 'copy';

            toaster.success(translations.unlinked);
            return element;
        }

        toaster.error(translations.unlinkFailed);

        return {state: 'error'};
    }

    /**
     *
     * @param {CalendarPublishElement} element
     * @returns {Promise<CalendarPublishElement>}
     */

    async reject(element) {
        const api = new API(API_ROUTE.REJECT);

        const data = {
            project: Laravel.activeProject,
            id: element.id
        };

        const result = await api.post(data);

        if (!result.errored) {
            toaster.success(translations.rejected);

            return element;
        }

        toaster.error(translations.rejectionFailed);

        // TODO error handling?
    }

    /**
     *
     * @param layerId {Number | String}
     * @param elements {CalendarPublishElement[]}
     * @param resolution {String}
     * @param ignoreValidity {Boolean}
     * @returns {Promise<void>}
     */
    async preview(layerId, elements, resolution, ignoreValidity) {
        const visible = {};

        elements.forEach((element) => {
            if (!element.data.hidden) {
                visible[element.id] = 1;
            }
        });

        const data = {
            project: Laravel.activeProject,
            user: Laravel.activeUser,
            id: layerId,
            visible,
            limitScreen: this.screen,
            resolution: resolution,
            ignoreValidity
        };

        const api = new API(API_ROUTE.PREVIEW);
        const result = await api.post(data);

        EaseEventBus.fire('updatePreviewProperties', ({url: result.url, aspect: result.aspect}));
    }
}
